import {Box, Typography} from '@mui/material';
import AurIconButton from '../../common/aur-button/AurIconButton';
import AurAccordion from '../../common/aur-accordion/AurAccordion';
import StackedWorkspaces from './StackedWorkspaces';
import {t} from 'i18next';

import {
  ProjectEditFlag,
  ProjectDetails,
} from '../../pages/user-management/models';
import ClientProjectSelection from './ClientProjectSelection';
import styles from '../.././../styles/components/userManagement/clientInfoTab.module.scss';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import AurHighlyConfidential from '../../common/aur-highly-confidential/AurHighlyConfidential';
import {ClientProject} from '../../../types/project-type/projectDetailsType';
import {PermissionGroup} from '../../../types/permission-group/permissionGroup';
import HostCountryDetails from '../../aur-host-country-details/AurHostCountryDetails';
import {ItemType} from '../../pages/user-management/tabs/AddClientInfo';
import PermissionGroupDialog from '../../permission-groups/PermissionGroupDialog';
import {useState} from 'react';
import {resolvePermissionGroupItemType} from '../../../utils/permissionGroupItemUtils';
import {useSelector} from 'react-redux';
import {permissionGroupSelector} from '../../../redux/slices/permissionGroupSlice';
import AurButton from '../../common/aur-button/AurButton';
import {PERMISSION_GROUP_CATEGORY} from '../../../constants/constants';

type HeaderButtonGroupProp = {
  onEditClick?: () => void;
  onTrashClick?: () => void;
  showEdit: boolean;
};
type AddTeamMemberAccordionProp = {
  headerTitle: string;
  project: ProjectDetails;
  editButtonClicked?: (projectEditFlag: ProjectEditFlag) => void;
  deleteButtonClicked?: (projectId: string) => void;
  editMode?: ProjectEditFlag;
  clientProjects: ClientProject[];
  clientHCProjects: ClientProject[];
  showHCProjects: boolean;
  editModeSubmitClick?: (
    project: ProjectDetails,
    previousProjectId?: string
  ) => void;
  unsavedProjects?: ProjectDetails[];
  hideAccordionHeaderButtonGroup?: boolean;
  isAdmin?: boolean;
  selectedPermissionGroup?: PermissionGroup | null;
  selectedClientId?: string;
  selectedClientBudgetEstimatedPermisionEnable?: boolean;
  permissionOptionList: ItemType[];
  onPermissionChange?: (value: string) => void;
  onCreateNewPermissionGroup?: (clientId: string) => void;
};

const HeaderButtonGroup = ({
  onEditClick,
  onTrashClick,
  showEdit,
}: HeaderButtonGroupProp) => {
  return (
    <Box className={`${styles.accordionHeaderButtonGroup}`}>
      <AurIconButton
        ariaLabel="delete"
        size="big"
        shape
        icon={<AurIcon icon={IconNames.Remove} />}
        onClick={() => onTrashClick && onTrashClick()}
        dataTestId="delete-button"
      />
      {showEdit ? (
        <AurIconButton
          ariaLabel="edit"
          size="big"
          shape
          icon={<AurIcon icon={IconNames.Edit} />}
          onClick={() => onEditClick && onEditClick()}
          dataTestId="edit-button"
        />
      ) : (
        <></>
      )}
    </Box>
  );
};
const AddTeamMemberAccordion = ({
  headerTitle,
  project,
  deleteButtonClicked,
  editButtonClicked,
  editMode,
  clientProjects,
  showHCProjects,
  clientHCProjects,
  editModeSubmitClick,
  unsavedProjects,
  hideAccordionHeaderButtonGroup = false,
  isAdmin = false,
  selectedClientId,
  selectedClientBudgetEstimatedPermisionEnable,
  permissionOptionList,
  onPermissionChange,
  onCreateNewPermissionGroup,
}: AddTeamMemberAccordionProp) => {
  const [selectedPermissionGroupDetails, setSelectedPermissionGroupDetails] =
    useState<ItemType>();
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const permissionGroupsState = useSelector(permissionGroupSelector);
  const trashClickHandler = () => {
    deleteButtonClicked && deleteButtonClicked(project.projectId);
  };

  const editModeCancelClickHandler = () => {
    editButtonClicked &&
      editButtonClicked({projectId: project.projectId, editMode: false});
  };

  const editClickHandler = () => {
    editButtonClicked &&
      editButtonClicked({
        projectId: project.projectId,
        editMode: true,
      });
  };

  const isEditMode = () => {
    if (!editMode) return false;

    if (
      editMode.editMode &&
      editMode.projectId.toLowerCase() == project.projectId.toLowerCase()
    )
      return true;
    return false;
  };

  const projectEditMode = (
    <Box className={`${styles.projectAccrodionEditPanel}`}>
      <ClientProjectSelection
        clientProjects={clientProjects}
        clientHCProjects={clientHCProjects}
        onAddThisProjectClick={editModeSubmitClick}
        onCancelClick={editModeCancelClickHandler}
        defaultSelectedProject={project}
        unsavedProjects={unsavedProjects}
        sectionTitleHidden={true}
        showHCProjects={showHCProjects}
        selectedClientId={selectedClientId}
        selectedClientBudgetEstimatedPermisionEnable={
          selectedClientBudgetEstimatedPermisionEnable
        }
        projectItemsVisible={2}
        anchorBottonPosition={true}
        permissionOptionList={permissionOptionList}
        onPermissionChange={(e) => {
          onPermissionChange && onPermissionChange(e);
        }}
        onCreateNewPermissionGroup={(e) => {
          onCreateNewPermissionGroup && onCreateNewPermissionGroup(e);
        }}
      />
    </Box>
  );

  const openPermissionGroupDetails = (
    permissionGroupId: string | undefined
  ) => {
    setSelectedPermissionGroupDetails(
      resolvePermissionGroupItemType(
        permissionGroupId,
        permissionOptionList,
        permissionGroupsState.permissionGroups
      )
    );
    setShowPermissionModal(true);
  };

  const viewMode = (
    <Box className="add-team-member-data">
      <StackedWorkspaces
        headerText={t('common.workspacesCount', {
          count: project.workspaces.length,
        })}
        workspaces={project.workspaces}
      />
      <Box>
        <Typography className="typo-text subhead4_size heading-color">
          {project?.permissionGroup}
        </Typography>
        {project?.permissionGroupId && (
          <AurButton
            ariaLabel={t('addEditTeamMember.form.permissionGroupDetails')}
            disabled={false}
            onClick={() =>
              openPermissionGroupDetails(project.permissionGroupId)
            }
            type="text"
            size="Small"
            label={t('addEditTeamMember.form.permissionGroupDetails')}
            classNames="remove-padding"
          />
        )}
      </Box>
    </Box>
  );

  const titleContent = (
    <>
      {!hideAccordionHeaderButtonGroup && !isEditMode() ? (
        <HeaderButtonGroup
          onEditClick={editClickHandler}
          onTrashClick={trashClickHandler}
          showEdit={!isAdmin}
        />
      ) : (
        ''
      )}
    </>
  );

  return (
    <AurAccordion
      alertWarningIcon={false}
      className={`${styles.clientInfoAccordion} add-team-member-accordion-container`}
      title={headerTitle}
      titleContent={titleContent}
    >
      {project.highlyConfidential ? (
        <AurHighlyConfidential
          size="small"
          type="pill"
          showWarningText={true}
        />
      ) : (
        <></>
      )}

      <Box className="add-team-member-data-host">
        <HostCountryDetails {...project} />
      </Box>

      {!isAdmin && isEditMode() ? projectEditMode : <></>}

      {!isAdmin && !isEditMode() ? viewMode : <></>}
      <PermissionGroupDialog
        open={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
        permissionModalData={selectedPermissionGroupDetails}
        permCategory={PERMISSION_GROUP_CATEGORY.PROJECT}
      />
    </AurAccordion>
  );
};

export default AddTeamMemberAccordion;
